import { FinancialDocumentListItem } from "@common/types/financialDocument";
import {
    FinancialDocumentView,
    FinancialDocumentSortValue,
} from "@common/types/views/financialDocumentViews";
import { View } from "@common/types/views/views";
import { useCallback, useMemo } from "react";
import type { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import classNames from "classnames";
import { useFinancialDocumentBulkSelection } from "../FinancialDocumentBulkSelection/useFinancialDocumentBulkSelection";
import {
    ViewTable,
    type ViewTableRowDragOut,
} from "@design-system/ViewTable/ViewTable";
import type { ViewTableLoadMore } from "@design-system/ViewTable/ViewTableLoadMore";
import type { ViewTableMeta } from "@design-system/ViewTable/ViewTable.lib";
import { useDocumentRowHighlight } from "@components/tasks/useDocumentRowHighlight";
import { useLoadedRowIdResolver } from "@components/tasks/useRefRowHighlight";
import styles from "./FinancialDocumentsView.module.scss";
import { financialDocumentsViewConfig } from "./financialDocumentsViewConfig";
import {
    FinancialDocumentsTableCallbacks,
    FinancialDocumentsTableOptions,
} from "./useIsFinancialDocumentsTableReadonly";

interface Props {
    view: FinancialDocumentView;
    documents: FinancialDocumentListItem[] | null;
    onDocumentClick?: (document?: FinancialDocumentListItem) => void;
    sort?: FinancialDocumentSortValue;
    onSortChange?: (sort: FinancialDocumentSortValue) => void;
    emptyView?: () => React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    onViewChange?: (view: View) => void;
    rowDragOut?: ViewTableRowDragOut<FinancialDocumentListItem>;
    loadMore?: ViewTableLoadMore;
}

export const FinancialDocumentsView: React.FC<Props> = ({
    view,
    documents,
    onDocumentClick,
    sort,
    onSortChange,
    emptyView = () => "No documents found",
    footer,
    className,
    onViewChange,
    rowDragOut,
    loadMore,
}) => {
    const data = documents ?? null;

    const bulkSelection = useFinancialDocumentBulkSelection();

    const enableRowSelection = useMemo(() => {
        if (!view.options?.bulkActions) {
            return false;
        }
        // Disable individual row selection when "select all" is active
        if (bulkSelection.hasSelectedByCurrentFilters) {
            return false;
        }
        return () => true; // All documents can be selected
    }, [view.options?.bulkActions, bulkSelection.hasSelectedByCurrentFilters]);

    const rowSelection = useMemo(() => {
        if (!bulkSelection) {
            return {};
        }

        const selection: RowSelectionState = {};

        if (bulkSelection.hasSelectedByCurrentFilters) {
            documents?.forEach((document) => {
                selection[document.id] = true;
            });
        } else {
            bulkSelection.selected.forEach((document) => {
                selection[document.id] = true;
            });
        }

        return selection;
    }, [bulkSelection, documents]);

    const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(
        (updater) => {
            if (!data) {
                return;
            }

            let nextSelection: RowSelectionState;
            if (typeof updater === "function") {
                nextSelection = updater(rowSelection);
            } else {
                nextSelection = updater;
            }

            const selectedIds = Object.keys(nextSelection).filter(
                (id) => nextSelection[id],
            );

            const selectedDocuments = data.filter((document) =>
                selectedIds.includes(String(document.id)),
            );

            if (selectedIds.length === 0) {
                bulkSelection.clearSelection();
            } else {
                bulkSelection.clearSelection();
                bulkSelection.select(selectedDocuments);
            }
        },
        [bulkSelection, data, rowSelection],
    );

    const handleRowClick = useCallback(
        (document?: FinancialDocumentListItem) => {
            onDocumentClick?.(document);
        },
        [onDocumentClick],
    );

    const tableMeta = useMemo(
        (): ViewTableMeta<
            FinancialDocumentsTableCallbacks,
            FinancialDocumentsTableOptions
        > => ({
            callbacks: {
                onDocumentClick,
            },
            options: {
                readonly: view.options?.readonly,
            },
        }),
        [onDocumentClick, view.options?.readonly],
    );

    const resolveTableRowId = useLoadedRowIdResolver(data);
    const { viewTableRef, selectedRowId } = useDocumentRowHighlight({
        resolveTableRowId,
    });

    return (
        <ViewTable
            ref={viewTableRef}
            selectedRowId={selectedRowId}
            className={classNames(styles.financialDocumentsView, className)}
            data={data}
            view={view as View}
            onViewChange={onViewChange}
            config={financialDocumentsViewConfig}
            onRowClick={handleRowClick}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            enableRowSelection={enableRowSelection}
            sort={sort}
            onSortChange={onSortChange}
            emptyView={emptyView}
            tableMeta={tableMeta}
            footer={footer}
            enableKeyboardShortcuts
            fixedTopOffsetPx
            rowDragOut={rowDragOut}
            loadMore={loadMore}
        />
    );
};
